import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pwLoading, setPwLoading] = useState(false);

  // 2FA
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(true);
  const [setupData, setSetupData] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [twoFaMsg, setTwoFaMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDisable, setShowDisable] = useState(false);

  useEffect(() => {
    api.get2faStatus()
      .then(data => setTwoFaEnabled(data.enabled))
      .finally(() => setTwoFaLoading(false));
  }, []);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);

    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: t('settings.passwordMismatch') });
      return;
    }
    if (newPassword.length < 8) {
      setPwMsg({ type: 'error', text: t('settings.passwordTooShort') });
      return;
    }

    setPwLoading(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setPwMsg({ type: 'success', text: t('settings.passwordUpdated') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwMsg({ type: 'error', text: err.message });
    } finally {
      setPwLoading(false);
    }
  };

  const handleSetup2fa = async () => {
    setTwoFaMsg(null);
    try {
      const data = await api.setup2fa();
      setSetupData(data);
    } catch (err: any) {
      setTwoFaMsg({ type: 'error', text: err.message });
    }
  };

  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFaMsg(null);
    try {
      await api.verify2fa(verifyCode);
      setTwoFaEnabled(true);
      setSetupData(null);
      setVerifyCode('');
      setTwoFaMsg({ type: 'success', text: t('settings.2faEnabledSuccess') });
    } catch (err: any) {
      setTwoFaMsg({ type: 'error', text: err.message });
    }
  };

  const handleDisable2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFaMsg(null);
    try {
      await api.disable2fa(disablePassword);
      setTwoFaEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
      setTwoFaMsg({ type: 'success', text: t('settings.2faDisabledSuccess') });
    } catch (err: any) {
      setTwoFaMsg({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      {/* Change Password */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
        <h3 className="text-white font-semibold text-base mb-1">{t('settings.changePassword')}</h3>
        <p className="text-white/30 text-sm mb-5">{t('settings.changePasswordDesc')}</p>

        {pwMsg && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            pwMsg.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>{pwMsg.text}</div>
        )}

        <form onSubmit={handlePasswordChange} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm text-white/50 mb-1.5">{t('settings.currentPassword')}</label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm outline-none focus:border-[#d4a237]/50 focus:ring-1 focus:ring-[#d4a237]/20"
            />
          </div>
          <div>
            <label className="block text-sm text-white/50 mb-1.5">{t('settings.newPassword')}</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm outline-none focus:border-[#d4a237]/50 focus:ring-1 focus:ring-[#d4a237]/20"
            />
          </div>
          <div>
            <label className="block text-sm text-white/50 mb-1.5">{t('settings.confirmPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm outline-none focus:border-[#d4a237]/50 focus:ring-1 focus:ring-[#d4a237]/20"
            />
          </div>
          <button
            type="submit"
            disabled={pwLoading}
            className="self-start px-6 py-2.5 bg-gradient-to-r from-[#f6d77a] via-[#d4a237] to-[#a87d2a] rounded-lg text-[#0b0b10] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {pwLoading ? t('settings.updating') : t('settings.updatePassword')}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-base">{t('settings.2faTitle')}</h3>
          {!twoFaLoading && (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              twoFaEnabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/[0.06] text-white/40'
            }`}>
              {twoFaEnabled ? t('settings.2faEnabled') : t('settings.2faDisabled')}
            </span>
          )}
        </div>
        <p className="text-white/30 text-sm mb-5">{t('settings.2faDesc')}</p>

        {twoFaMsg && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            twoFaMsg.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>{twoFaMsg.text}</div>
        )}

        {twoFaLoading ? (
          <div className="text-white/30 text-sm">Loading...</div>
        ) : twoFaEnabled ? (
          // Enabled state
          <div>
            <div className="flex items-center gap-3 p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl mb-4">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 12 15 16 10" />
              </svg>
              <span className="text-emerald-400 text-sm font-medium">{t('settings.2faProtected')}</span>
            </div>

            {showDisable ? (
              <form onSubmit={handleDisable2fa} className="flex flex-col gap-3">
                <label className="block text-sm text-white/50">{t('settings.2faEnterPassword')}</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={e => setDisablePassword(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm outline-none focus:border-red-500/50"
                />
                <div className="flex gap-3">
                  <button type="submit" className="px-5 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30">{t('settings.2faDisableBtn')}</button>
                  <button type="button" onClick={() => setShowDisable(false)} className="px-5 py-2 bg-white/[0.06] text-white/50 rounded-lg text-sm hover:bg-white/[0.1]">{t('common.cancel')}</button>
                </div>
              </form>
            ) : (
              <button onClick={() => setShowDisable(true)} className="px-5 py-2 bg-white/[0.06] text-white/50 rounded-lg text-sm hover:bg-white/[0.1]">
                {t('settings.2faDisableBtn')}
              </button>
            )}
          </div>
        ) : setupData ? (
          // Setup flow
          <div>
            <p className="text-white/60 text-sm mb-4">{t('settings.2faScanQr')}</p>

            <div className="flex flex-col items-center gap-4 p-5 bg-white rounded-xl mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpauthUrl)}`}
                alt="2FA QR Code"
                width="200"
                height="200"
              />
            </div>

            <div className="mb-4 p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg">
              <span className="text-xs text-white/40 block mb-1">{t('settings.2faManualKey')}</span>
              <code className="text-[#e8c062] font-mono text-sm tracking-widest">{setupData.secret}</code>
            </div>

            <form onSubmit={handleVerify2fa} className="flex gap-3">
              <input
                type="text"
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('settings.2faEnterCode')}
                required
                maxLength={6}
                className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm font-mono tracking-widest text-center outline-none focus:border-[#d4a237]/50"
              />
              <button type="submit" className="px-6 py-2.5 bg-gradient-to-r from-[#f6d77a] via-[#d4a237] to-[#a87d2a] rounded-lg text-[#0b0b10] text-sm font-semibold hover:opacity-90">
                {t('settings.2faVerify')}
              </button>
            </form>
          </div>
        ) : (
          // Not enabled, not setting up
          <button onClick={handleSetup2fa} className="px-6 py-2.5 bg-gradient-to-r from-[#f6d77a] via-[#d4a237] to-[#a87d2a] rounded-lg text-[#0b0b10] text-sm font-semibold hover:opacity-90">
            {t('settings.2faSetup')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Settings;
